import React from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { useGetOrganisationsQuery, useGetPlansQuery } from "../../../../services";
import { useAppSelector } from "../../../../redux/hooks";
import { selectOrganisationId } from "../../../../redux/organisationReducer";

import { RootState } from "../../../../redux/store";

interface ComparePlansModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ComparePlansModal: React.FC<ComparePlansModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [billingCycle, setBillingCycle] = React.useState<"monthly" | "annual">(
    "monthly"
  );
  const { isLoggedIn } = useAppSelector((state: RootState) => state.admin);
  const organisationId = useAppSelector(selectOrganisationId);
  const { data: organisationsResp, isLoading: orgsLoading } =
    useGetOrganisationsQuery(undefined, {
      skip: !isLoggedIn,
    });
  // Fetch plans
  const { data: plansResp, isLoading: plansLoading } = useGetPlansQuery(
    undefined,
    {
      skip: !isLoggedIn,
    }
  );

  const allOrgs: any[] = Array.isArray((organisationsResp as any)?.data)
    ? (organisationsResp as any).data
    : Array.isArray(organisationsResp)
      ? (organisationsResp as any[])
      : [];

  const selectedOrg = allOrgs.find((org) => org._id === organisationId);
  const currency = selectedOrg?.currency?.toUpperCase() || "INR";

  const plans: any[] = Array.isArray((plansResp as any)?.results)
    ? (plansResp as any).results
    : Array.isArray((plansResp as any)?.data?.results)
      ? (plansResp as any).data.results
      : Array.isArray(plansResp)
        ? (plansResp as any[])
        : [];

  const getPlan = (
    type: "BASIC" | "PREMIUM",
    variant: "LICENSE" | "ADDON_SCREEN",
    cycle: "MONTHLY" | "ANNUALLY"
  ) => {
    return plans.find(
      (p) =>
        String(p?.type)?.toUpperCase() === type &&
        String(p?.variant)?.toUpperCase() === variant &&
        String(p?.cycle)?.toUpperCase() === cycle
    );
  };

  const getPrice = (
    type: "BASIC" | "PREMIUM",
    variant: "LICENSE" | "ADDON_SCREEN",
    cycle: "MONTHLY" | "ANNUALLY"
  ) => {
    const plan = getPlan(type, variant, cycle);
    if (!plan) return 0;

    const prices = Array.isArray(plan.prices) ? plan.prices : [];
    const priceObj =
      prices.find((x: any) => x?.currency?.toUpperCase() === currency) ||
      prices[0];
    return Number(priceObj?.price ?? 0);
  };

  const getScreens = (
    type: "BASIC" | "PREMIUM",
    variant: "LICENSE" | "ADDON_SCREEN",
    cycle: "MONTHLY" | "ANNUALLY"
  ) => {
    const plan = getPlan(type, variant, cycle);
    return plan?.screens || 0;
  };

  const currentPrices = {
    basic: getPrice(
      "BASIC",
      "LICENSE",
      billingCycle === "monthly" ? "MONTHLY" : "ANNUALLY"
    ),
    premium: getPrice(
      "PREMIUM",
      "LICENSE",
      billingCycle === "monthly" ? "MONTHLY" : "ANNUALLY"
    ),
    addonBasic: getPrice(
      "BASIC",
      "ADDON_SCREEN",
      billingCycle === "monthly" ? "MONTHLY" : "ANNUALLY"
    ),
    addonPremium: getPrice(
      "PREMIUM",
      "ADDON_SCREEN",
      billingCycle === "monthly" ? "MONTHLY" : "ANNUALLY"
    ),
  };

  const currentScreens = {
    basic: getScreens(
      "BASIC",
      "LICENSE",
      billingCycle === "monthly" ? "MONTHLY" : "ANNUALLY"
    ),
    premium: getScreens(
      "PREMIUM",
      "LICENSE",
      billingCycle === "monthly" ? "MONTHLY" : "ANNUALLY"
    ),
  };

  const currencySymbols: Record<string, string> = {
    INR: "₹",
    AED: "د.إ",
    USD: "$",
  };
  const currencySymbol = currencySymbols[currency] || currency;

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isDark ? "bg-black/10" : "bg-black/40"
        }`}
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-5xl rounded-2xl shadow-2xl border ${isDark
          ? "bg-[#151515] text-gray-100 border-[#2C2C2C]"
          : "bg-white text-gray-900 border-gray-200"
          } max-h-[90vh] overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className={`absolute top-3 right-4 p-2 rounded-full ${isDark ? "hover:bg-white/10" : "hover:bg-black/5"
            }`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
          </svg>
        </button>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto">
          {/* Billing toggle */}
          <div className="flex justify-center mb-6">
            <div
              className={`inline-flex items-center rounded-full p-[2px] ${isDark ? "bg-[#262626]" : "bg-gray-200"
                }`}
            >
              <button
                type="button"
                onClick={() => setBillingCycle("monthly")}
                className={`px-8 py-1.5 text-[11px] font-semibold rounded-full transition-all ${billingCycle === "monthly"
                  ? "text-black shadow-sm"
                  : isDark
                    ? "text-gray-200"
                    : "text-gray-700"
                  }`}
                style={
                  billingCycle === "monthly"
                    ? {
                      background:
                        "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.90) 0%, rgba(83, 91, 16, 0.90) 100%)",
                    }
                    : undefined
                }
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle("annual")}
                className={`px-8 py-1.5 text-[11px] font-semibold rounded-full transition-all ${billingCycle === "annual"
                  ? "text-black shadow-sm"
                  : isDark
                    ? "text-gray-200"
                    : "text-gray-700"
                  }`}
                style={
                  billingCycle === "annual"
                    ? {
                      background:
                        "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.90) 0%, rgba(83, 91, 16, 0.90) 100%)",
                    }
                    : undefined
                }
              >
                Annual
              </button>
            </div>
          </div>

          {/* Plan cards row */}
          <div className="grid gap-4 md:grid-cols-3 mb-6">
            {/* Addon Screen */}
            <div
              className={`rounded-xl border px-4 py-4 flex flex-col justify-between ${isDark
                ? "bg-[#242424] border-[#3A3A3A]"
                : "bg-gray-100 border-gray-300"
                }`}
            >
              <div>
                <h3 className="text-sm font-semibold text-gray-200 mb-1">
                  Addon Screen
                </h3>
                <p className="text-[11px] text-gray-400 mb-3">
                  For Basic License
                </p>
                <div className="mb-3">
                  <div className="text-xs text-gray-400">For Basic License</div>
                  <div className="flex items-baseline gap-1 text-sm font-semibold text-white">
                    <span>{currencySymbol}{currentPrices.addonBasic}</span>
                    <span className="text-[10px] font-normal text-gray-400">
                      /{billingCycle === "monthly" ? "mo" : "yr"}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">
                    For Premium License
                  </div>
                  <div className="flex items-baseline gap-1 text-sm font-semibold text-white">
                    <span>{currencySymbol}{currentPrices.addonPremium}</span>
                    <span className="text-[10px] font-normal text-gray-400">
                      /{billingCycle === "monthly" ? "mo" : "yr"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Basic Screen */}
            <div
              className={`rounded-xl border px-6 py-6 flex flex-col justify-between ${isDark
                ? "bg-[#232323] border-[#3A3A3A]"
                : "bg-[#F3F4F6] border-gray-300"
                }`}
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Basic Screen
                </h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-bold text-white">
                    {currencySymbol}{currentPrices.basic}
                  </span>
                  <span className="text-xs text-gray-400">/{billingCycle === "monthly" ? "mo" : "yr"}</span>
                </div>
              </div>
              <button
                type="button"
                className="mt-2 w-full h-10 rounded-[10px] text-xs font-semibold text-black border"
                style={{
                  background:
                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.90) 0%, rgba(83, 91, 16, 0.90) 100%)",
                  border: "1px solid var(--menu-active-border, #C6D92C)",
                }}
              >
                Subscribe Now
              </button>
            </div>

            {/* Premium Screen */}
            <div
              className={`rounded-xl border px-6 py-6 flex flex-col justify-between ${isDark
                ? "bg-[#232323] border-[#3A3A3A]"
                : "bg-[#F3F4F6] border-gray-300"
                }`}
            >
              <div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Premium Screen
                </h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-bold text-white">
                    {currencySymbol}{currentPrices.premium}
                  </span>
                  <span className="text-xs text-gray-400">/{billingCycle === "monthly" ? "mo" : "yr"}</span>
                </div>
              </div>
              <button
                type="button"
                className="mt-2 w-full h-10 rounded-[10px] text-xs font-semibold text-black border"
                style={{
                  background:
                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.90) 0%, rgba(83, 91, 16, 0.90) 100%)",
                  border: "1px solid var(--menu-active-border, #C6D92C)",
                }}
              >
                Subscribe Now
              </button>
            </div>
          </div>

          {/* Features comparison table */}
          <div
            className={`rounded-xl border overflow-hidden ${isDark ? "border-[#2A2A2A]" : "border-gray-200"
              }`}
          >
            <table className="w-full text-xs">
              <thead className={isDark ? "bg-[#2A2A2A]" : "bg-gray-100"}>
                <tr>
                  <th className="px-6 py-3 text-left font-semibold text-gray-200">
                    Features
                  </th>
                  <th className="px-6 py-3 text-center font-semibold text-gray-200">
                    Basic Screen
                  </th>
                  <th className="px-6 py-3 text-center font-semibold text-gray-200">
                    Premium Screen
                  </th>
                </tr>
              </thead>
              <tbody
                className={
                  isDark
                    ? "bg-[#151515] divide-y divide-[#2A2A2A]"
                    : "bg-white divide-y divide-gray-200"
                }
              >
                {/* White Labeling */}
                <tr>
                  <td className="px-6 py-3 text-gray-300">
                    <div className="flex items-center gap-2">
                      White Labeling
                      <span className="w-4 h-4 rounded-full border border-gray-500 flex items-center justify-center text-[9px] text-gray-400">
                        ?
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-center text-gray-500 text-base">
                    –
                  </td>
                  <td className="px-6 py-3 text-center">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#C6D92C]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 13L10 18L19 7"
                          stroke="#111111"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </td>
                </tr>

                {/* Number of screens */}
                <tr>
                  <td className="px-6 py-3 text-gray-300">
                    <div className="flex items-center gap-2">
                      Number of screens
                      <span className="w-4 h-4 rounded-full border border-gray-500 flex items-center justify-center text-[9px] text-gray-400">
                        ?
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-center text-gray-200">{currentScreens.basic}</td>
                  <td className="px-6 py-3 text-center">
                    <span className="inline-flex items-center gap-1 justify-center text-gray-200">
                      <span>{currentScreens.premium}</span>
                    </span>
                  </td>
                </tr>

                {/* Admin Dashboard */}
                <tr>
                  <td className="px-6 py-3 text-gray-300">
                    <div className="flex items-center gap-2">
                      Admin Dashboard
                      <span className="w-4 h-4 rounded-full border border-gray-500 flex items-center justify-center text-[9px] text-gray-400">
                        ?
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-center">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#C6D92C]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 13L10 18L19 7"
                          stroke="#111111"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </td>
                  <td className="px-6 py-3 text-center">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#C6D92C]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 13L10 18L19 7"
                          stroke="#111111"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </td>
                </tr>

                {/* Addon screens */}
                <tr>
                  <td className="px-6 py-3 text-gray-300">
                    <div className="flex items-center gap-2">
                      Addon screens
                      <span className="w-4 h-4 rounded-full border border-gray-500 flex items-center justify-center text-[9px] text-gray-400">
                        ?
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-center text-[#C6D92C] font-medium">
                    Unlimited
                  </td>
                  <td className="px-6 py-3 text-center text-[#C6D92C] font-medium">
                    Unlimited
                  </td>
                </tr>

                {/* Customizable schedule */}
                <tr>
                  <td className="px-6 py-3 text-gray-300">
                    <div className="flex items-center gap-2">
                      Customizable schedule
                      <span className="w-4 h-4 rounded-full border border-gray-500 flex items-center justify-center text-[9px] text-gray-400">
                        ?
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-center text-gray-500 text-base">
                    –
                  </td>
                  <td className="px-6 py-3 text-center">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#C6D92C]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 13L10 18L19 7"
                          stroke="#111111"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </td>
                </tr>

                {/* Trainer Mobile App */}
                <tr>
                  <td className="px-6 py-3 text-gray-300">
                    <div className="flex items-center gap-2">
                      Trainer Mobile App
                      <span className="w-4 h-4 rounded-full border border-gray-500 flex items-center justify-center text-[9px] text-gray-400">
                        ?
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-center">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#C6D92C]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 13L10 18L19 7"
                          stroke="#111111"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </td>
                  <td className="px-6 py-3 text-center">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#C6D92C]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 13L10 18L19 7"
                          stroke="#111111"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparePlansModal;
